import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/app-links";
import { BLOG_POSTS } from "@/data/blog";
import { SITE_URL, robotsForHost, homepageFaqLd } from "@/lib/site-seo";

const BASE = SITE_URL;

type Meta = { title: string; description: string; name: string };

// Public, indexable pages only. Private app routes get noindex.
export const PUBLIC_META: Record<string, Meta> = {
  "/blog": { name: "Food-safety blog", title: "Restaurant Food-Safety Blog — Crystal HACCP", description: "Practical restaurant guides to temperature records, opening and closing checks, corrective actions, multi-location oversight and Norwegian IK-mat documentation." },
  ...Object.fromEntries(BLOG_POSTS.map((post) => [`/blog/${post.slug}`, { name: post.title, title: post.title, description: post.description }])),
  "/": { name: "Home", title: "Crystal HACCP — Food Safety Management for Restaurants", description: "HACCP compliance, temperature logs, cleaning checks, incidents and audit-ready reports. Web dashboard plus iOS and Android app." },
  "/pricing": { name: "Pricing", title: "Pricing — Crystal HACCP", description: "Starter $34.99, Growth $49.99 and Pro $59.99 per unit per month. 7-day free trial. Annual billing includes one free month." },
  "/platform": { name: "Platform", title: "The Crystal HACCP Platform — Web Dashboard and Mobile App", description: "Manage your food-safety system on the web and complete checks on iOS and Android. Records stay connected across both." },
  "/regional-compliance": { name: "Regional Compliance", title: "Regional Food Safety Compliance — Crystal HACCP", description: "Select a region per location to load the supported law pack, guidance and procedures. Changes are reviewed before they are applied." },
  "/industries": { name: "Industries", title: "Industries — Restaurants, Cafes, Catering, Food Production | Crystal HACCP", description: "HACCP software for restaurants, cafes, catering businesses and food manufacturers." },
  "/multi-location": { name: "Multi-Location", title: "Multi-Location Food Safety Management — Crystal HACCP", description: "See checks, exceptions and open corrective actions across every location from one dashboard." },
  "/resources": { name: "Resources", title: "Resources — Crystal HACCP", description: "Guides, mobile app downloads and contact information for Crystal HACCP." },
  "/download-app": { name: "Download the App", title: "Crystal HACCP App for iOS and Android", description: "Daily checks, temperature records, cleaning checks, inspections and corrective actions on iPhone, iPad and Android." },
  "/audit-check": { name: "Inspection readiness check", title: "Free food safety inspection readiness check | Crystal HACCP", description: "Answer 8 quick questions and get a readiness score plus the record-keeping gaps an inspector is most likely to spot. Free, no sign-up." },
  "/ik-mat": { name: "IK-mat app", title: "IK-mat app – digital internkontroll for mat | Crystal HACCP", description: "Temperaturlogg, renhold, avvik og rapporter klare for Mattilsynet. App for iPhone og Android, laget i Oslo. 7 dagers gratis prøveperiode." },
  "/haccp-app": { name: "HACCP App", title: "HACCP App for Restaurants, Cafes and Catering — Crystal HACCP", description: "Temperature logs, cleaning checks, corrective actions and audit-ready PDF reports on iPhone, iPad, Android and web. 7-day free trial." },
  "/sfbb-alternative": { name: "SFBB Alternative", title: "Digital Safer Food, Better Business Alternative — Crystal HACCP", description: "Replace the SFBB paper diary with a phone app: daily checks, temperature records, cleaning and corrective actions, exportable for inspections." },
  "/temperature-log": { name: "Temperature Log", title: "Digital Fridge and Freezer Temperature Log — Crystal HACCP", description: "Log fridge, freezer and hot-holding temperatures from a phone, flag deviations and export the history as PDF." },
  "/features/risk-assessment": { name: "Risk Assessment", title: "HACCP Risk Assessment — Crystal HACCP", description: "HACCP risk scoring and critical control point tracking." },
  "/features/operational-intelligence": { name: "Operational Intelligence", title: "Food Safety Compliance Analytics — Crystal HACCP", description: "Compliance dashboards and analytics across your sites." },
  "/features/automated-scheduling": { name: "Automated Scheduling", title: "Automated HACCP Checklist Scheduling — Crystal HACCP", description: "Recurring food-safety checklists scheduled automatically for every unit." },
  "/services/implementation": { name: "Implementation", title: "HACCP Implementation Services — Crystal HACCP", description: "Onboarding and setup help for Crystal HACCP." },
  "/services/training": { name: "Training", title: "HACCP Staff Training — Crystal HACCP", description: "Staff HACCP training programs." },
  "/services/compliance": { name: "Compliance", title: "Compliance and Audit Support — Crystal HACCP", description: "Regulatory and audit preparation support." },
  "/privacy-policy": { name: "Privacy Policy", title: "Privacy Policy — Crystal HACCP", description: "How Crystal HACCP collects and uses personal data." },
  "/terms-of-service": { name: "Terms of Service", title: "Terms of Service — Crystal HACCP", description: "Terms for using Crystal HACCP." },
  "/cookies": { name: "Cookie Policy", title: "Cookie Policy — Crystal HACCP", description: "How Crystal HACCP uses cookies." },
  "/data-protection": { name: "Data Protection", title: "Data Protection — Crystal HACCP", description: "How Crystal HACCP protects your data." },
};

const offers = [
  ["Starter", "34.99"],
  ["Growth", "49.99"],
  ["Pro", "59.99"],
].map(([name, price]) => ({
  "@type": "Offer",
  name: `${name} (per unit, monthly)`,
  price,
  priceCurrency: "USD",
  url: `${BASE}/pricing`,
}));

export const softwareAppLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Crystal HACCP",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  url: `${BASE}/`,
  downloadUrl: [APP_STORE_URL, GOOGLE_PLAY_URL],
  description: "Food safety management for restaurants, cafes, catering and food manufacturers: HACCP checklists, temperature records, incidents, corrective actions and audit exports.",
  offers,
};

export default function RouteSeo() {
  const { pathname } = useLocation();
  const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  const meta = PUBLIC_META[path];
  const robots = robotsForHost(window.location.hostname, Boolean(meta));

  if (!meta) {
    return (
      <Helmet>
        <meta name="robots" content={robots} />
      </Helmet>
    );
  }

  const url = `${BASE}${path === "/" ? "/" : path}`;
  const ld: object[] = [];
  if (path === "/") ld.push(homepageFaqLd);
  const post = BLOG_POSTS.find((item) => path === `/blog/${item.slug}`);
  if (post) ld.push({
    "@context": "https://schema.org", "@type": "Article",
    headline: post.title, description: post.description, inLanguage: post.lang,
    datePublished: post.published,
    author: { "@type": "Organization", name: "Crystal HACCP", url: `${BASE}/` },
    publisher: { "@type": "Organization", name: "Crystal HACCP", url: `${BASE}/` },
    mainEntityOfPage: url,
  });
  if (path === "/" || path === "/pricing" || path === "/download-app" || ["/ik-mat","/haccp-app","/sfbb-alternative","/temperature-log"].includes(path)) ld.push(softwareAppLd);
  if (path !== "/") {
    ld.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        ...(post ? [{ "@type": "ListItem", position: 2, name: "Food-safety blog", item: `${BASE}/blog` }] : []),
        { "@type": "ListItem", position: post ? 3 : 2, name: meta.name, item: url },
      ],
    });
  }

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={post ? "article" : "website"} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {ld.map((d, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(d)}</script>
      ))}
    </Helmet>
  );
}
